import React from "react";
import { useAuth } from '../../auth';
import { Link, Redirect } from 'react-router-dom';
import { Loading } from '../index';
import { useRouter } from 'components';

// Member type to path mapping
const memberTypeToPath = {
  1: { path: "/members/entered-apprentice", title: "Entered Apprentice" },
  2: { path: "/members/fellow-craft", title: "Fellow Craft" },
  3: { path: "/members/master-mason", title: "Master Mason" },
  18: { path: "/members/rose-croix", title: "Rose Croix" },
  30: { path: "/members/knights-kadosh", title: "Knights Kadosh" },
  31: { path: "/members/tribunal-of-courage", title: "Tribunal of Courage" },
  32: { path: "/members/consistory", title: "Consistory" },
  33: { path: "/members/baringa-triangle", title: "Baringa Triangle" }
};

// Admin role to path mapping
const roleToPath = {
  "adelaide 406": { path: "/freemasonry/adelaide-406", title: "4. Adelaide" },
  "aurora australis 431": { path: "/freemasonry/aurora-australis-431", title: "5. Aurora" },
  "avalon 1718": { path: "/freemasonry/avalon-1718", title: "6. Avalon" },
  "ouroboros 2175": { path: "/freemasonry/ouroboros-2175", title: "7. Ouroboros" },
  "sydney 404": { path: "/freemasonry/sydney-404", title: "8. Sydney" },
  "national": { path: "/freemasonry/national", title: "1. National" },
  "secretaries": { path: "/freemasonry/secretaries", title: "2. Secretaries" },
  "treasurers": { path: "/freemasonry/treasurers", title: "3. Treasurers" },
  "victoria 403": { path: "/freemasonry/victoria-403", title: "Victoria 403" },
};

export function DashboardLayout() {
  const auth = useAuth();
  const session = auth.sessionV2 || auth.session || auth.user;
  const router = useRouter();

  // Get user data directly from session (populated during signin via signinV2)
  // This data comes from fetchUserProfileAndRoles() called during authentication
  const userMemberType = session?.memberType;
  const userRoles = session?.roles || [];

  // Helper function to get available member paths based on member type
  const getAvailableMemberPaths = (memberType) => {
    if (!memberType) return [];
    
    const availablePaths = [];
    
    // Special case for Admin (33) - has access to everything
    if (memberType === 33) {
      return Object.entries(memberTypeToPath).map(([type, config]) => ({
        ...config,
        type: parseInt(type)
      }));
    }
    
    // For regular member types, show paths they have access to (their level and below)
    Object.entries(memberTypeToPath).forEach(([type, config]) => {
      const requiredType = parseInt(type);
      if (memberType >= requiredType) {
        availablePaths.push({
          ...config,
          type: requiredType
        });
      }
    });
    
    return availablePaths;
  };

  // Get available admin paths based on roles
  const getAvailableAdminPaths = (roles) => {
    if (!Array.isArray(roles) || roles.length === 0) return [];

    const getOrder = (title) => {
      const match = /^(\d+)\./.exec(title);
      return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
    };

    return roles
      .map(role => roleToPath[role.toLowerCase()])
      .filter(Boolean)
      .map(config => ({ ...config, role: true }))
      .sort((a, b) => getOrder(a.title) - getOrder(b.title));
  };

  const availableMemberPaths = getAvailableMemberPaths(userMemberType);
  const availableAdminPaths = getAvailableAdminPaths(userRoles);

  // If user has no access to anything, show message
  if (availableMemberPaths.length === 0 && availableAdminPaths.length === 0) {
    return (
      <div className="container" style={{maxWidth: '800px', marginInline: 'auto', padding: '2rem'}}>
        <h1>Access Denied</h1>
        <p>You do not have access to any member or admin areas.</p>
        <Link to="/">Return to Home</Link>
      </div>
    );
  }

  return (
    <div className="container" style={{maxWidth: '1000px', marginInline: 'auto', padding: '2rem'}}>
      <h1>Dashboard</h1>
      
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginTop: '2rem'}}>
        
        {/* Member Areas Section */}
        {availableMemberPaths.length > 0 && (
          <div style={{background: '#f8f9fa', padding: '1.5rem', borderRadius: '8px', border: '1px solid #dee2e6'}}>
            <h2 style={{marginTop: 0, color: '#495057'}}>Member Areas</h2>
            <p style={{color: '#6c757d', fontSize: '0.9rem', marginBottom: '1rem'}}>
              Member Type: {userMemberType ? memberTypeToPath[userMemberType]?.title || `Level ${userMemberType}` : 'Not Set'}
            </p>
            <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
              {availableMemberPaths.map((config, index) => (
                <li key={config.path} style={{marginBottom: '0.5rem'}}>
                  <Link 
                    to={config.path}
                    style={{
                      display: 'block',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'white',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      color: '#495057',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#e9ecef';
                      e.target.style.borderColor = '#adb5bd';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#dee2e6';
                    }}
                  >
                    {config.title}
                    <small style={{display: 'block', color: '#6c757d', fontSize: '0.8rem'}}>
                      Level {config.type}
                    </small>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Admin Areas Section */}
        {availableAdminPaths.length > 0 && (
          <div style={{background: '#fff3cd', padding: '1.5rem', borderRadius: '8px', border: '1px solid #ffeaa7'}}>
            <h2 style={{marginTop: 0, color: '#856404'}}>Admin Areas</h2>
            <p style={{color: '#856404', fontSize: '0.9rem', marginBottom: '1rem'}}>
              Roles: {userRoles.join(', ')}
            </p>
            <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
              {availableAdminPaths.map((config, index) => (
                <li key={config.path} style={{marginBottom: '0.5rem'}}>
                  <Link 
                    to={config.path}
                    style={{
                      display: 'block',
                      padding: '0.75rem 1rem',
                      backgroundColor: 'white',
                      border: '1px solid #ffeaa7',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      color: '#856404',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#fff9e6';
                      e.target.style.borderColor = '#ffd93d';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.borderColor = '#ffeaa7';
                    }}
                  >
                    {config.title}
                    <small style={{display: 'block', color: '#856404', fontSize: '0.8rem'}}>
                      Admin Access
                    </small>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div style={{marginTop: '2rem', marginBottom:'4rem', padding: '1.5rem', background: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff'}}>
        <h3 style={{marginTop: 0, marginBottom: '0.5rem', color: '#004085'}}>Quick Actions</h3>
        <div style={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
          <Link 
            to="/profile" 
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#c2a56b',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            View Profile
          </Link>
          <Link 
            to="/" 
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#6c757d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            Back to Home
          </Link>
          <button 
            onClick={() => auth.signoutAdmin().then(() => {
              router.push('/login');
            }).catch(() => {
              router.push('/login');
            })}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
} 